import * as UserSelectors   from './user-selectors';
import * as TimeHelpers     from '../../helpers/time-helpers';
import { checkRebrandName } from '../../helpers/pure-functions';
import BlurpleSquareLogo from '../../assets/imgs/brand/rebrand-square-logo-blurple.svg';
import RedSquareLogo     from '../../assets/imgs/brand/rebrand-square-logo-red.svg';
import { getQknSubscription } from './bill-selectors';

export const getShortName = (prod) => {
  switch (prod) {
    case 'quicken-ca':
    case 'quicken-us':
      return 'Quicken Classic';
    case 'acme':
    case 'acme-hab':
      return 'Simplifi';
    case 'billmanager':
    case 'billmanager-promotion':
      return 'Bill Manager';
    case 'quicken-estate-manager':
      return 'Quicken LifeHub';
    case 'quicken-online-backup':
      return 'Online Backup';
    default:
      return '';
  }
};

const getProdList = country => [
  'acme',
  `quicken-${country}`,
  'billmanager',
  'quicken-estate-manager',
  'quicken-online-backup'
];

export const getAccountMessage = (globalState) => {

  return getProdList(globalState.country).reduce((acm, cur) => {
    const SUB = getSubscription(globalState, cur);
    if (!SUB){
      return acm;
    }
    const {active, autoRenew, expireOn} = SUB;

    const HAS_ACH    = UserSelectors.getUserHasAch(globalState);
    const HAS_CREDIT = UserSelectors.getUserHasCredit(globalState);
    const NAME       = getShortName(cur);
    const IS_BM      = cur === 'billmanager';

    if (active && !TimeHelpers.isWithin45Days(expireOn)) return acm;
    else if (!active) {
      return {
        ...acm,
        issue:    !acm.issue ? 'ACTIVITY' : 'COMBINED',
        messages: [
          !acm.issue
            ? `Your ${checkRebrandName(NAME)} subscription is no longer active`
            : 'Some of your subscriptions need your attention'
        ]
      };
    } else if (!autoRenew || (IS_BM && !HAS_ACH) || (!IS_BM && !HAS_CREDIT)) {
      return {
        ...acm,
        issue:    !acm.issue ? 'SUBSCRIPTION' : 'COMBINED',
        messages: [
          !acm.issue
            ? `Your ${checkRebrandName(NAME)} subscription is expiring in ${TimeHelpers.daysLeft(expireOn)} days.`
            : 'Some of your subscriptions need your attention'
        ]
      };
    } else {
      return acm;
    }

  }, {type: 'ERROR', messages: [], issue: null});
};

export const getAlertInfo = (globalState) => {
  const ALERTS = getProdList(globalState.country).reduce((acm, cur) => {
    const SUB = getSubscription(globalState, cur);

    if (!SUB) return acm;

    const {active, autoRenew, expireOn} = SUB;

    const HAS_PAYMENT = UserSelectors.getProductsPaymentMethod(cur, globalState);

    if (
      !active ||
      (active && autoRenew && HAS_PAYMENT) ||
      (!TimeHelpers.isWithin15Days(expireOn))
    ) return acm;

    return [
      ...acm, {
        product: cur,
        title:   `Your ${getShortName(cur)} subscription is expiring in ${TimeHelpers.daysLeft(SUB.expireOn)} days.`,
        issue:   !autoRenew ? 'AUTORENEW' : 'PAYMENT',
        message: !autoRenew
          ? 'In order to not lose your connected services, you need to resume your subscription.'
          : 'In order to not lose your connected services, add a payment method for renewal'
      }
    ];
  }, []);

  const simplifiAlerts = ALERTS.filter((alert) => alert.product == 'acme');

  if(ALERTS.length === 1) {
    return ALERTS[0];
  } else if (ALERTS.length > 1 && simplifiAlerts.length === 1) {
    return simplifiAlerts[0];
  } else if (ALERTS.length > 1) {
    return ALERTS[0];
  } else {
    return null;
  }
};

export const getProductName = (globalState, product) => {
  const {tierName = '', productLineName = ''} = getSubscription(globalState, product) || {};
  switch (product) {
    case 'acme':
      return 'Simplifi';
    case 'quicken-us':
    case 'quicken-ca':
      return `${tierName}`;
    case 'billmanager':
      return `${productLineName} ${tierName}`;
    case 'quicken-estate-manager':
      return `${productLineName}`;
    case 'quicken-online-backup':
      return `${productLineName}`;
    default:
      return '';
  }
};

export const getProductLogo = (sub, isBundle=false) => {
  const redLogoProductLines = [
    'quicken-us',
    'quicken-ca',
    'quicken-online-backup',
    'billmanager',
    'billmanager-promotion'
  ];

  if(isBundle) {
    const products = sub?.subscriptions?.flatMap(product => product.productLineUriName);
    const hasRedLogoProduct = products?.some(item => redLogoProductLines.includes(item));
    return hasRedLogoProduct ? RedSquareLogo : BlurpleSquareLogo;
  } else {
    const product = typeof(sub) === 'string' ? sub : sub?.productLineUriName;
    return redLogoProductLines.includes(product) ? RedSquareLogo : BlurpleSquareLogo;
  }
};

export const getPriceBySku = (globalState, sku, tierUriName) => {
  const country = globalState?.country;

  if(globalState?.skus?.[sku]) {
    return globalState?.skus?.[sku]?.Price;
  } else if (tierUriName) {
    const defaultSkuByTier = globalState.defaultSkus?.[country]?.[tierUriName];
    return globalState?.skus?.[defaultSkuByTier]?.Price;
  } else {
    return '';
  }
};

// export const getQknPrice = (globalState, uriName) =>
//   `$${globalState.tierPrices?.[uriName]}`;

export const uriNameToSku = (uriName) => {
  switch (uriName?.toLowerCase()) {
    case 'plus':
      return '170329';
    case 'volume50':
      return '170327';
    case 'volume100':
      return '170330';
    case 'volume200':
      return '170331';

    // bm promotional skus:
    case 'lite':
      return '170433';
    case 'basic':
      return '170434';
    case 'plusTrial':
      return '170435';

    case 'qob-basic':
      return '170515';

    default:
      return null;
  }
};

export const getSubscription = (globalState, product, txnId=false) => {
  if(product === 'bundle' && txnId) {
    const bundleSub = globalState?.subscriptions?.bundles?.find(sub => sub?.txnId === txnId);
    if (bundleSub) { return bundleSub; }
  } else {
    return globalState?.subscriptions?.[product];
  }
};

export const getCoTermSubs = (globalState) => Object.values(globalState?.subscriptions).filter(sub => sub.isCoTerminus);

export const getAcmeProduct = (globalState) => {
  const SUBS = Object.keys(globalState.subscriptions);
  if (SUBS.includes('acme')) {
    return 'acme';
  } else if (SUBS.includes('acme-apple')) {
    return 'acme-apple';
  } else if (SUBS.includes('acme-google')) {
    return 'acme-google';
  } else {
    return null;
  }
};

export const getProducts = globalState => {
  const HAS_QKN = !!getSubscription(globalState, `quicken-${globalState.country}`);
  const HAS_QOB = !!getSubscription(globalState, 'quicken-online-backup');
  const HAS_QEM = !!getSubscription(globalState, 'quicken-lifehub');
  const HAS_BM  = !!getSubscription(globalState, 'billmanager');

  return {
    quicken:      HAS_QKN ? `quicken-${globalState.country}` : null,
    simplifi:     getAcmeProduct(globalState),
    onlineBackup: HAS_QEM ? 'quicken-online-backup' : null,
    lifeHub:      HAS_QEM ? 'quicken-lifehub' : null,
    billmanager:  HAS_BM ? 'billmanager' : null
  };
};

export const getDiscountedDataBySku = (globalState, sku) =>{
  return {DisplayFullPrice : globalState?.skus?.[sku]?.DisplayFullPrice, DisplayDiscount : globalState?.skus?.[sku]?.DisplayDiscount};
};

export const mergeSubscription = (globalState, product, data) => ({
  ...globalState.subscriptions,
  [product]: {
    ...globalState.subscriptions[product],
    ...data
  }
});

export const getEffectiveMoment = (globalState, product) => {
  return getSubscription(globalState, product)?.['effectiveAt'] || null;
};

export const simplifiTierDisplayName = (tierName, nameType = false) => {
  // check for the old names and abort the rest of the function for backward compatibility since the api responses changed after we have already deployed
  // This function should be removed once the production tier names have been changed
  const oldNames = [
    'Simplifi - Basic',
    'Simplifi - Premier',
    'Simplifi - Home and Business',
  ];
  if (!oldNames.includes(tierName)) {
    return tierName;
  }
  const tierNameToFullDisplayName = {
    'Simplifi - Basic': 'Quicken Simplifi',
    'Simplifi - Premier': 'Quicken Premier',
    'Simplifi - Home and Business': 'Quicken Business & Personal',
  };

  const tierNameToDisplayName = {
    'Simplifi - Basic': 'Simplifi',
    'Simplifi - Premier': 'Premier',
    'Simplifi - Home and Business': 'Business & Personal',
  };

  if (nameType == 'fullname') {
    return tierNameToFullDisplayName[tierName] || 'Quicken Simplifi';
  } else {
    return tierNameToDisplayName[tierName] || 'Quicken Simplifi';
  }
};


export const getDowngradeProductPoints = (tierUriName) => {
  const qknHnbPoints = [
    'Managing & viewing all your finances in one place.',
    'Invoicing tools to get you paid faster',
    'Built -in business tax schedules to make tax time easier',
    'Smart reports for better financial decisions, like P & L and cashflow',
    'Custom tools for rental properties',
  ];

  const qknPremierPoints = [
    'Custom watchlists',
    'Assess impact of buys/sells* & Morningstar fund rating',
    'Premium phone & chat support',
    'Bill pay included in subscription',
    'Deduction finder* & FBAR tax report',
  ];

  const qknDeluxePoints = [
    'Saving goals',
    '12 - month budget',
    'Investment analysis tools, like performance & cost basis',
    'Lifetime Planner',
    'Tax Schedules A, B & D',
  ];

  const simpQbpPoints = [
    'Managing & viewing all your finances in one place.',
    'Invoicing tools to get you paid faster',
    'Built -in business tax schedules to make tax time easier',
    'Smart reports for better financial decisions, like P&L and cashflow',
  ];

  const productPointMapping = {
    'deluxe': qknDeluxePoints,
    'premier': qknPremierPoints,
    'hab-rpm': qknHnbPoints,
    'hab': qknHnbPoints,
    'acme-hab': simpQbpPoints,
  };

  if (!productPointMapping[tierUriName]) {
    console.warn(`No downgrade points for ${tierUriName}.`);
  }
  return productPointMapping[tierUriName] || [];

};

export const getArCancelModalTitle = (sub) => {
  let subToUse = false;

  if (sub?.bundleInfo) {
    const priority = ['quicken-us', 'quicken-ca', 'acme'];
    const selected = priority
      .map(p => sub?.subscriptions?.find(sub => sub.productLine === p))
      .find(Boolean);
    subToUse = selected;
  } else {
    subToUse = sub;
  }

  const defaultTitle = 'Did you know that...';
  if(!subToUse) return defaultTitle;

  const qobTitle = 'Do you really want to cancel Quicken Online Backup renewal?';
  const simplifiTitle = `Do you really want to cancel Quicken ${simplifiTierDisplayName(subToUse?.tierName, 'fullname')} renewal?`;

  // If a product line is not in here it will just use the default title
  const productTitleMapping = {
    'acme': simplifiTitle,
    'acme-basic': simplifiTitle,
    'acme-hab': simplifiTitle,

    'quicken-online-backup': qobTitle,
  };

  return productTitleMapping[subToUse?.tierUriName] || defaultTitle;
};

export const getArCancelProductPoints = (sub) => {
  let subToUse = false;

  /*
  If we're getting points for a bundle we need to check which sub to use, the 'main' sub
  (quicken classic or simplifi) should always be used, if for some reason the bundle has both,
  then we should use simplifi over quicken classic
  */
  if (sub?.subscriptions) {
    const priority = [
      'acme',
      'quicken-us',
      'quicken-ca',
    ];
    const selected = priority
      .map(p => sub?.subscriptions?.find(sub => sub.productLine === p))
      .find(Boolean);
    subToUse = selected;
  } else {
    subToUse = sub;
  }

  const defaultPoints = [
    'Bank Downloads',
    'Free Phone Support',
    'Stock Price Updates',
    'Regular Updates, Fixes, and Improvements',
    'Account Sync'
  ];
  if(!subToUse) return defaultPoints;

  const simplifiQbpPoints = [
    'Managing business & personal finances, all from one app',
    'Automatically syncing financial transactions',
    'Creating & sending invoices',
    'Making tax prep easier',
    'Generating business reports',
    'Free phone & chat support',
  ];

  const qemPoints = [
    'Your Quicken LifeHub data',
    'Sharing info with family',
    'Organizing essential info',
    'Syncing with Quicken finance software',
    'Preparing for emergencies',
  ];

  const qobPoints = [
    'Automatic data backup',
    'Secure data storage',
    'Easy data restoration',
    'Peace of mind',
    'Quicken integration'
  ];

  // Tier's/product lines not in here will just use the default cancellation points
  const productPointMapping = {
    'billmanager': [], // Need to show nothing for billmanager
    'acme-hab': simplifiQbpPoints,

    'quicken-estate-manager': qemPoints,
    'qem-basic': qemPoints,

    'quicken-online-backup': qobPoints,
    'qob-basic': qobPoints,
  };

  return productPointMapping[subToUse?.tierUriName] || defaultPoints;
};


// Not currently available in any API's so hardcoded here, these are the product offering for different
// product lines that we show on the change plan modals, ordering matters, higher tiers go on the bottom.
export const getChangePlanProductOfferings = (product) => {
  const ACME_PLANS = [
    { sku: '170538', uriName: 'acme-hab', productLineUriName: 'acme', name: 'Business & Personal' },
    { sku: '170522', uriName: 'acme-basic', productLineUriName: 'acme', name: 'Simplifi' }
  ];

  const CA_QKN_PLANS = [
    { sku: '170195', uriName: 'hab', productLineUriName: 'quicken-ca', name: 'Business & Personal' },
    { sku: '170194', uriName: 'deluxe', productLineUriName: 'quicken-ca', name: 'Deluxe' },
    { sku: '170193', uriName: 'cash-manager', productLineUriName: 'quicken-ca', name: 'Starter' }
  ];

  const US_QKN_PLANS = [
    { sku: '170166', uriName: 'hab-rpm', productLineUriName: 'quicken-us', name: 'Business & Personal' },
    { sku: '170164', uriName: 'premier', productLineUriName: 'quicken-us', name: 'Premier' },
    { sku: '170165', uriName: 'deluxe', productLineUriName: 'quicken-us', name: 'Deluxe' },
    { sku: '170163', uriName: 'starter', productLineUriName: 'quicken-us', name: 'Starter' }
  ];

  if (product === 'acme') {
    return ACME_PLANS;
  } else if (product === 'quicken-us') {
    return US_QKN_PLANS;
  } else if (product === 'quicken-ca') {
    return CA_QKN_PLANS;
  } else {
    return [];
  }
};

export const hasSpecificProducts = (state, coTermProductLines) => {
  return coTermProductLines?.length === 2 && coTermProductLines.includes('quicken-online-backup') && coTermProductLines.includes(`quicken-${state.country}`);
};

// A ridiculous name for a ridiculous function
export const splitCotermGroupsAndActLikeQuickenAndObAreNotCotermWhenTheyReallyAre = (cotermSubs) => {
  const groups = new Map();
  cotermSubs.forEach(sub => {
    const key = JSON.stringify(sub.coTermProductLines.sort());
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(sub);
  });
  return Array.from(groups.values()).filter(group => !hasSpecificProducts(group[0].coTermProductLines));
};

// More rediculous names for more ridiculous functions
export const groupByTxnIdAndPretendBundledSubsAreOneSubWhenTheyAreReallyArentAndOurApisSuck = (bundledSubs, skuData) => {
  const bundleRenewalSku = bundledSubs?.[0]?.bundleInfo?.renewalSku;
  const renewalSkuData = skuData[bundleRenewalSku];

  return Object.values(
    bundledSubs.reduce((acc, item) => {
      const txnId = item.bundleInfo?.txnId;
      if (!txnId) return acc;

      if (!acc[txnId]) {
        acc[txnId] = {
          txnId,
          bundleSkuData: renewalSkuData,
          bundleSub: {
            active: item?.active,
            autoRenew: item?.autoRenew,
            currentTerm: item?.currentTerm,
            effectiveAt: item?.effectiveAt,
            expireAt: item?.expireAt,
            expireOn: item?.expireOn,
            remindLater: item?.remindLater,
            renewalFrequency: item?.renewalFrequency,
            isBundle: true,
          },
          subscriptions: [],
        };
      }

      acc[txnId].subscriptions.push(item);
      return acc;
    }, {})
  );
};




export const getQobHasEntitlement = (globalState) => {
  const QKN_SUB = getQknSubscription(globalState, globalState?.country);
  const QOB_ENT = QKN_SUB?.allEntitlements?.includes('QOB_BASIC');
  return QOB_ENT ? true : false;
};

// This is shitty code
export const getShittyHardcodedQobData = (globalState) => {
  const QKN_SUB = getQknSubscription(globalState, globalState?.country);
  const QOB_ENT = QKN_SUB?.allEntitlements?.includes('QOB_BASIC');

  const fakeSubData = {
    'productName': 'Quicken Online Backup',
    'productLineName': 'Quicken Online Backup',
    'productLineUriName': 'quicken-online-backup',
    'tierName': 'QOB - Basic',
    'tierUriName': 'qob-basic',
    'active': QKN_SUB?.active,
    'autoRenew': QKN_SUB?.autoRenew,
    'effectiveAt': QKN_SUB?.effectiveAt,
    'expireAt': QKN_SUB?.expireAt,
    'expireOn': QKN_SUB?.expireOn,
    'createdAt': QKN_SUB?.createdAt,
    'isSubscriptionAddOn': true
  };

  return QOB_ENT ? fakeSubData : false;
};
